import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FEATURE_FLAGS, can } from '@brandschutz/shared';
import { ALLE_NAV_ITEMS } from '@/components/layout/nav-items';
import { Button } from '@/components/ui/Button';
import { Icon, type IconName } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth-context';
import { useTheme } from '@/lib/theme';
import { useIsFeatureEnabled } from '@/lib/feature-flags';
// Direkt aus dem Modul statt aus dem Barrel: der smart-search-Barrel zieht die
// gesamte Palette samt Such-API mit, gebraucht wird nur der Offen-Zustand.
import { useCommandPalette } from '@/skills/smart-search/context';
import {
  BEFEHLS_BEISPIELE,
  erkenneBefehl,
  erkenneBestaetigung,
  navigationszieleAus,
  type ErkannterBefehl,
} from './befehle';
import { useSprachsteuerung } from './context';
import { useSpracherkennung, type ErkennungStatus } from './use-spracherkennung';
import { FEHLER_TEXTE } from './web-speech';

/**
 * Globale Sprachsteuerung — Bedienfeld am unteren Bildschirmrand.
 *
 * Bewusst KEIN modaler Dialog: die Sprachsteuerung läuft neben der Arbeit,
 * die Seite dahinter bleibt mit Maus und Tastatur voll bedienbar. Sprache ist
 * ein zusätzlicher Weg, nie der einzige.
 *
 * Was der Nutzer sieht, ist Absicht: Spracherkennung verhört sich, also muss
 * jederzeit sichtbar sein, WAS verstanden wurde — und vor allem, was daraufhin
 * passieren soll. Ausführende Befehle laufen erst nach einer Rückfrage.
 */

interface Meldung {
  id: number;
  art: 'gehoert' | 'ausgefuehrt' | 'unklar' | 'hinweis';
  text: string;
  vorschlaege?: string[];
}

const MELDUNGS_ICON: Record<Meldung['art'], IconName> = {
  gehoert: 'Mic',
  ausgefuehrt: 'Check',
  unklar: 'HelpCircle',
  hinweis: 'Info',
};

const STATUS_TEXT: Record<ErkennungStatus, string> = {
  'nicht-unterstuetzt': 'Nicht verfügbar',
  inaktiv: 'Bereit',
  'hoert-zu': 'Hört zu …',
  verarbeitet: 'Wertet aus …',
  fehler: 'Fehler',
};

const STATUS_ICON: Record<ErkennungStatus, IconName> = {
  'nicht-unterstuetzt': 'MicOff',
  inaktiv: 'Mic',
  'hoert-zu': 'Square',
  verarbeitet: 'Loader2',
  fehler: 'MicOff',
};

/** Wie viele Rückmeldungen im Bedienfeld stehen bleiben. */
const MAX_MELDUNGEN = 4;

export function SprachsteuerungPanel() {
  const aktiviert = useIsFeatureEnabled(FEATURE_FLAGS.VOICE_CONTROL);
  const { offen, setOffen, umschalten } = useSprachsteuerung();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toggle: designUmschalten } = useTheme();
  const { setOpen: setSchnellsucheOffen } = useCommandPalette();

  const [meldungen, setMeldungen] = useState<Meldung[]>([]);
  const [bestaetigung, setBestaetigung] = useState<ErkannterBefehl | null>(null);
  const meldungsZaehler = useRef(0);
  const bestaetigungRef = useRef<ErkannterBefehl | null>(null);
  bestaetigungRef.current = bestaetigung;
  const panelRef = useRef<HTMLDivElement>(null);

  // Nur Bereiche anbieten, die der Nutzer auch betreten darf — sonst schickt
  // die Sprachsteuerung ihn in die „Kein Zugriff“-Seite.
  const navigationsziele = useMemo(() => {
    const erlaubt = ALLE_NAV_ITEMS.filter(
      (item) => !item.permission || (user ? can(user.role, item.permission) : false),
    );
    return navigationszieleAus(erlaubt);
  }, [user]);

  const melde = useCallback((art: Meldung['art'], text: string, vorschlaege?: string[]) => {
    meldungsZaehler.current += 1;
    const neu: Meldung = { id: meldungsZaehler.current, art, text, vorschlaege };
    setMeldungen((bisher) => [...bisher, neu].slice(-MAX_MELDUNGEN));
  }, []);

  const fuehreAus = useCallback(
    (befehl: ErkannterBefehl) => {
      setBestaetigung(null);
      melde('ausgefuehrt', befehl.beschriftung);

      if (befehl.art === 'navigation') {
        navigate(befehl.ziel);
        return;
      }
      if (befehl.art === 'suche') {
        navigate(`/projects?search=${encodeURIComponent(befehl.begriff)}`);
        return;
      }

      switch (befehl.aktion) {
        case 'projekt-anlegen':
          navigate('/projects/new');
          return;
        case 'schnellsuche-oeffnen':
          setSchnellsucheOffen(true);
          return;
        case 'zurueck':
          navigate(-1);
          return;
        case 'design-umschalten':
          designUmschalten();
          return;
        case 'seitenleiste-umschalten':
          // Ehrlich statt stillschweigend: der Zustand der Seitenleiste liegt
          // in deren eigenem Speicher und ist von hier aus nicht erreichbar.
          melde(
            'hinweis',
            'Die Seitenleiste lässt sich noch nicht per Sprache steuern — der Pfeil am unteren Rand der Seitenleiste klappt sie ein.',
          );
          return;
        case 'abmelden':
          void logout().then(() => navigate('/login', { replace: true }));
          return;
        case 'sprachsteuerung-beenden':
          setOffen(false);
          return;
      }
    },
    [designUmschalten, logout, melde, navigate, setOffen, setSchnellsucheOffen],
  );

  const verarbeite = useCallback(
    (roh: string) => {
      melde('gehoert', roh);

      const offeneRueckfrage = bestaetigungRef.current;
      if (offeneRueckfrage) {
        const antwort = erkenneBestaetigung(roh);
        if (antwort === 'ja') {
          fuehreAus(offeneRueckfrage);
          return;
        }
        if (antwort === 'nein') {
          setBestaetigung(null);
          melde('hinweis', 'Abgebrochen — es wurde nichts ausgeführt.');
          return;
        }
        melde('hinweis', 'Bitte mit „ja“ oder „nein“ antworten.');
        return;
      }

      const ergebnis = erkenneBefehl(roh, { navigationsziele });
      if (ergebnis.status === 'unklar') {
        melde('unklar', `Nicht verstanden: „${ergebnis.gehoert}“`, ergebnis.vorschlaege);
        return;
      }
      if (ergebnis.befehl.bestaetigungNoetig) {
        setBestaetigung(ergebnis.befehl);
        return;
      }
      fuehreAus(ergebnis.befehl);
    },
    [fuehreAus, melde, navigationsziele],
  );

  const erkennung = useSpracherkennung({ onEndergebnis: verarbeite });
  const { starte, stoppe, status, fehler, zwischentext } = erkennung;

  // Bedienfeld offen heißt: es wird zugehört. Geschlossen heißt: Mikrofon aus.
  useEffect(() => {
    if (!aktiviert) return;
    if (offen) starte();
    else stoppe();
  }, [aktiviert, offen, starte, stoppe]);

  // Tastenkürzel Alt+S — die Sprachbedienung muss ohne Maus erreichbar sein.
  useEffect(() => {
    if (!aktiviert) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        umschalten();
      } else if (e.key === 'Escape' && offen) {
        setOffen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aktiviert, offen, setOffen, umschalten]);

  // Beim Öffnen den Fokus ins Bedienfeld holen, damit Tastaturnutzer die
  // Rückfrage-Knöpfe direkt erreichen.
  useEffect(() => {
    if (!offen) return;
    const frame = requestAnimationFrame(() => panelRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [offen]);

  useEffect(() => {
    if (!offen) {
      setBestaetigung(null);
      setMeldungen([]);
    }
  }, [offen]);

  if (!aktiviert) return null;

  return (
    <AnimatePresence>
      {offen ? (
        <motion.div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-label="Sprachsteuerung"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.18 }}
          className="fixed bottom-4 right-4 z-40 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-ink-200 bg-paper shadow-elev-3 outline-none"
        >
          <div className="flex items-center gap-2 border-b border-ink-200 px-3 py-2">
            <Icon
              name={STATUS_ICON[status]}
              size={16}
              className={cn(
                status === 'verarbeitet' && 'animate-spin',
                status === 'hoert-zu' && 'animate-pulse',
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink-900">Sprachsteuerung</p>
              <p className="text-[11px] text-ink-500" role="status">
                {STATUS_TEXT[status]}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOffen(false)}
              aria-label="Sprachsteuerung schließen"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-500 hover:bg-ink-100"
            >
              <Icon name="X" size={16} />
            </button>
          </div>

          <div className="max-h-[60vh] space-y-3 overflow-y-auto px-3 py-3">
            {fehler ? (
              <div className="rounded border border-warning/40 bg-warning/10 p-2">
                <p className="text-xs text-ink-800">{FEHLER_TEXTE[fehler]}</p>
                <Button variant="secondary" size="sm" onClick={starte} className="mt-2">
                  Erneut versuchen
                </Button>
              </div>
            ) : null}

            {/* Was gerade gehört wird — der Nutzer muss mitlesen können. */}
            <p className="min-h-[1.25rem] text-sm italic text-ink-500" aria-live="polite">
              {zwischentext !== ''
                ? `„${zwischentext}“`
                : status === 'hoert-zu'
                  ? 'Sprechen Sie …'
                  : ''}
            </p>

            {bestaetigung ? (
              <div className="rounded border border-accent-500 bg-accent-100 p-2 dark:bg-accent-500/20">
                <p className="text-sm font-medium text-ink-900">
                  Ausführen: {bestaetigung.beschriftung}?
                </p>
                <p className="mt-0.5 text-[11px] text-ink-600">
                  Sagen Sie „ja“ oder „nein“ — oder wählen Sie hier.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    variant="accent"
                    size="sm"
                    iconLeft={<Icon name="Check" size={14} />}
                    onClick={() => fuehreAus(bestaetigung)}
                  >
                    Ja, ausführen
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<Icon name="X" size={14} />}
                    onClick={() => {
                      setBestaetigung(null);
                      melde('hinweis', 'Abgebrochen — es wurde nichts ausgeführt.');
                    }}
                  >
                    Nein
                  </Button>
                </div>
              </div>
            ) : null}

            <ul className="space-y-1.5">
              {meldungen.map((meldung) => (
                <li key={meldung.id} className="flex gap-2 text-xs">
                  <Icon
                    name={MELDUNGS_ICON[meldung.art]}
                    size={14}
                    className="mt-0.5 text-ink-400"
                  />
                  <div className="min-w-0">
                    <p
                      className={cn(
                        meldung.art === 'gehoert' ? 'text-ink-500' : 'text-ink-800',
                        meldung.art === 'ausgefuehrt' && 'font-medium',
                      )}
                    >
                      {meldung.text}
                    </p>
                    {meldung.vorschlaege && meldung.vorschlaege.length > 0 ? (
                      <p className="mt-0.5 text-[11px] text-ink-500">
                        Versuchen Sie: {meldung.vorschlaege.map((v) => `„${v}“`).join(' · ')}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            <details className="rounded border border-ink-200 bg-ink-50 p-2">
              <summary className="cursor-pointer text-xs font-medium text-ink-700">
                Das können Sie sagen
              </summary>
              <div className="mt-2 space-y-2">
                {BEFEHLS_BEISPIELE.map((gruppe) => (
                  <div key={gruppe.gruppe}>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-ink-500">
                      {gruppe.gruppe}
                    </p>
                    <p className="text-[11px] text-ink-700">
                      {gruppe.beispiele.map((b) => `„${b}“`).join(' · ')}
                    </p>
                  </div>
                ))}
                <p className="text-[11px] text-ink-500">
                  Außerdem jeder Menüpunkt beim Namen, z. B. „Vorlagen“ oder „Dokumente“.
                </p>
              </div>
            </details>
          </div>

          <div className="border-t border-ink-200 px-3 py-2">
            <p className="text-[10px] leading-snug text-ink-500">
              Die Aufnahme wird vom Spracherkennungsdienst des Browsers ausgewertet und dazu an
              dessen Server übertragen. Mikrofon nur einschalten, wenn das für die besprochenen
              Inhalte in Ordnung ist.
            </p>
            <p className="mt-1 text-[10px] text-ink-400">
              <kbd className="rounded border border-ink-200 bg-paper px-1 font-mono">Alt</kbd> +{' '}
              <kbd className="rounded border border-ink-200 bg-paper px-1 font-mono">S</kbd> öffnet
              und schließt die Sprachsteuerung.
            </p>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
